import apiClient, { ApiResponse, PaginatedApiResponse } from '../api/client';

export type IssueStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type IssueCategory = 'bribe' | 'wrong-charge' | 'technical' | 'other';

export interface Issue {
  id: number;
  reference: string;
  county_id?: number;
  county_name?: string;
  category: IssueCategory | string;
  subject: string | null;
  message: string;
  status: IssueStatus | string;
  reporter_type: 'citizen' | 'county_admin' | string;
  is_anonymous: boolean;
  reporter_name: string;
  contact_email?: string | null;
  touch_point?: string | null;
  handled_by?: string | null;
  resolution_note?: string | null;
  resolved_at?: string | null;
  created_at: string;
}

export interface IssueFilters {
  search?: string;
  status?: string;
  category?: string;
  county_id?: number;
  page?: number;
  per_page?: number;
}

export interface IssueSummary {
  open?: number;
  in_progress?: number;
  resolved?: number;
}

interface IssueListResponse extends PaginatedApiResponse<Issue> {
  summary?: IssueSummary;
}

function buildParams(filters?: IssueFilters): string {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

class IssueService {
  private county = '/admin/county/issues';
  private superadmin = '/admin/superadmin/issues';

  // County admin
  async list(filters?: IssueFilters): Promise<IssueListResponse> {
    // apiClient.get is typed as ApiResponse<T> (no meta); Laravel paginators return meta at the root.
    const res = await apiClient.get<Issue[]>(`${this.county}${buildParams(filters)}`);
    return res as unknown as IssueListResponse;
  }
  async create(data: { category: string; subject?: string; message: string }): Promise<ApiResponse<Issue>> {
    return apiClient.post(this.county, data);
  }
  async updateStatus(id: number, status: IssueStatus, resolution_note?: string): Promise<ApiResponse<Issue>> {
    return apiClient.put(`${this.county}/${id}/status`, { status, resolution_note });
  }

  // Superadmin (read-only, all counties)
  async listAll(filters?: IssueFilters): Promise<PaginatedApiResponse<Issue>> {
    const res = await apiClient.get<Issue[]>(`${this.superadmin}${buildParams(filters)}`);
    return res as unknown as PaginatedApiResponse<Issue>;
  }
}

export const issueService = new IssueService();
export default IssueService;
